
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import CountUp from "react-countup";
import { FaDownload, FaSync, FaTimes } from "react-icons/fa";
import {
  FiList, FiCheckCircle, FiXCircle, FiCalendar,
  FiTrendingUp, FiActivity, FiBriefcase, FiClipboard, FiSearch,
  FiAlertTriangle, FiShield, FiEye, FiBell
} from "react-icons/fi";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from "recharts";
import "./EmployeeDashboard.css";

const API_BASE_URL = 'https://ingrainhirebackend.ingrainsystems.com/api';
const API_DOMAIN = 'https://ingrainhirebackend.ingrainsystems.com';

// --- Components from RecruitmentDashboard ---

const StatCard = ({ icon: Icon, label, value, color, isPercentage }) => {
  const themes = {
    indigo: "border-indigo-500",
    emerald: "border-emerald-500",
    amber: "border-amber-500",
    rose: "border-rose-500",
    cyan: "border-cyan-500",
  };
  const currentTheme = themes[color] || themes.indigo;

  return (
    <div className={`bg-white rounded-xl border-l-4 ${currentTheme} shadow-sm p-4 flex items-center justify-between`}>
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-bold text-gray-800 mt-1">
          {isPercentage ? (
            <>
              <CountUp end={value} duration={1.5} decimals={1} />%
            </>
          ) : (
            <CountUp end={value} duration={1.5} separator="," />
          )}
        </p>
      </div>
      <div className={`p-3 rounded-lg bg-${color}-50`}>
        <Icon className={`text-${color}-500`} size={20} />
      </div>
    </div>
  );
};

// Tooltip component
const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="px-3 py-2 text-xs bg-white border border-gray-100 rounded-lg shadow-xl">
      <p className="font-bold text-gray-800 mb-0.5 leading-none">{d.label}</p>
      <p className="leading-none text-gray-500">Count: {d.val}</p>
    </div>
  );
};

// Quality Card component for metrics
const QualityCard = ({ label, value, total, color }) => {
  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;

  const colorClasses = {
    rose: "bg-rose-50 border-rose-100",
    amber: "bg-amber-50 border-amber-100",
    emerald: "bg-emerald-50 border-emerald-100",
    cyan: "bg-cyan-50 border-cyan-100",
    indigo: "bg-indigo-50 border-indigo-100",
    purple: "bg-purple-50 border-purple-100",
  };

  return (
    <div className={`p-2 px-3 rounded-xl border ${colorClasses[color] || colorClasses.indigo} shadow-sm`}>
      <div className="flex justify-between items-center mb-1">
        <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">{label}</span>
      </div>
      <div className="flex items-baseline justify-between">
        <span className="text-xl font-black text-gray-800">
          <CountUp end={value} duration={1.5} />
        </span>
        <span className="text-[10px] font-bold text-gray-600">{percentage}%</span>
      </div>
      <div className="w-full h-1 bg-white rounded-full mt-2 overflow-hidden">
        <div
          className={`h-full rounded-full ${color === 'rose' ? 'bg-rose-500' :
            color === 'amber' ? 'bg-amber-500' :
              color === 'emerald' ? 'bg-emerald-500' :
                color === 'cyan' ? 'bg-cyan-500' :
                  color === 'purple' ? 'bg-purple-500' :
                    'bg-indigo-500'
            }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
const AllMedicalCertificate = () => {
  const [certificates, setCertificates]         = useState([]);
  const [filteredCertificates, setFiltered]     = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [currentPage, setCurrentPage]           = useState(1);
  const [itemsPerPage, setItemsPerPage]         = useState(10);
  const [searchTerm, setSearchTerm]             = useState("");
  const [statusFilter, setStatusFilter]         = useState("all");
  const [roleFilter, setRoleFilter]             = useState("");
  const [roleOpen, setRoleOpen]                 = useState(false);
  const [statusOpen, setStatusOpen]             = useState(false);
  const [roleSearch, setRoleSearch]             = useState("");
  const [roles, setRoles]                       = useState([]);
  const roleRef = useRef(null);
  const statusRef = useRef(null);

  const getClientId = () =>
    localStorage.getItem("clientId") || localStorage.getItem("clientCustomId") || "";

  useEffect(() => {
    fetchCertificates();
    fetchRoles();
    const handler = e => {
      if (roleRef.current && !roleRef.current.contains(e.target)) setRoleOpen(false);
      if (statusRef.current && !statusRef.current.contains(e.target)) setStatusOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const clientId = getClientId();
      const url = clientId
        ? `${API_BASE_URL}/medical-certificates/all/${clientId}`
        : `${API_BASE_URL}/medical-certificates/all`;
      const res = await axios.get(url);
      if (res.data.success) {
        const data = res.data.data || [];
        setCertificates(data);
        setFiltered(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const clientId = getClientId();
      const res = await axios.get(
        clientId ? `${API_BASE_URL}/jobs/all/${clientId}` : `${API_BASE_URL}/jobs/all`
      );
      if (res.data.success) {
        const jobs = res.data.jobPosts || res.data.data || [];
        const names = Array.from(
          new Set(jobs.map(j => j.role).filter(Boolean))
        );
        setRoles(names.map((name, i) => ({ id: i, name })));
      }
    } catch (err) {
      console.error("Error fetching roles:", err);
    }
  };

  const getStatusInfo = (expiryDate) => {
    if (!expiryDate) return { label: "Unknown", color: "#9CA3AF", text: "#6B7280", bg: "#f3f4f6", border: "#d1d5db" };
    const d = Math.ceil((new Date(expiryDate) - new Date()) / 86400000);
    if (d < 0)   return { label: "Expired",       color: "#EF4444", text: "#b91c1c", bg: "#fef2f2", border: "#fecaca" };
    if (d <= 30) return { label: "Expiring Soon", color: "#F87171", text: "#dc2626", bg: "#fff1f2", border: "#fecdd3" };
    if (d <= 60) return { label: "Due in 2 Mo.",  color: "#FB923C", text: "#c2410c", bg: "#fff7ed", border: "#fed7aa" };
    return             { label: "Active",          color: "#10B981", text: "#047857", bg: "#ecfdf5", border: "#a7f3d0" };
  };

  const getStats = () => {
    const m = { Expired: 0, "Expiring Soon": 0, "Due in 2 Mo.": 0, Active: 0 };
    certificates.forEach(c => {
      const l = getStatusInfo(c.expiryDate).label;
      if (m[l] !== undefined) m[l]++;
    });
    return [
      { label: "EXPIRED",  val: m.Expired,           color: "#EF4444" },
      { label: "NEXT 30D", val: m["Expiring Soon"],  color: "#F87171" },
      { label: "NEXT 60D", val: m["Due in 2 Mo."],   color: "#FB923C" },
      { label: "ACTIVE",   val: m.Active,             color: "#10B981" },
    ];
  };

  // Apply filters whenever certificates or filter values change
  useEffect(() => {
    let filtered = [...certificates];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(c => {
        const name = (c.employeeName || c.candidateName || "").toLowerCase();
        const id = (c.employeeId || c.candidateId || "").toLowerCase();
        return name.includes(term) || id.includes(term);
      });
    }

    // Role filter
    if (roleFilter) {
      const roleFilterLower = roleFilter.toLowerCase().trim();
      filtered = filtered.filter(c => {
        const certRole = (c.jobId?.role || c.role || "").toLowerCase().trim();
        return certRole === roleFilterLower;
      });
    }

    // Status filter
    if (statusFilter !== "all") {
      const statusMap = {
        expired: "Expired",
        next_month: "Expiring Soon",
        "2_months": "Due in 2 Mo.",
        active: "Active"
      };
      const targetStatus = statusMap[statusFilter];
      filtered = filtered.filter(c => {
        const info = getStatusInfo(c.expiryDate);
        return info.label === targetStatus;
      });
    }

    setFiltered(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, roleFilter, certificates]);

  const handleSendReminder = async cert => {
    try {
      const recipientId = cert.employeeId || cert.candidateId;
      if (!recipientId) return alert("Recipient ID not found");
      await axios.post(`${API_BASE_URL}/medical-certificates/remind`, {
        recipientId,
        message: `RE-UPLOAD REQUIRED: Your medical certificate expires on ${new Date(cert.expiryDate).toLocaleDateString()}. Please upload a new one.`,
      });
      alert(`Reminder sent to ${cert.employeeName || cert.candidateName}`);
    } catch {
      alert("Failed to send reminder. Please try again.");
    }
  };

  const downloadCSV = () => {
    if (!filteredCertificates.length) return alert("No data to export!");
    const rows = [
      ["ID", "Name", "Reg. Date", "Exp. Date", "Status"].join(","),
      ...filteredCertificates.map(c => {
        const info = getStatusInfo(c.expiryDate);
        return [
          `"${c.employeeId || c.candidateId || "N/A"}"`,
          `"${c.employeeName || c.candidateName || "N/A"}"`,
          `"${new Date(c.registrationDate).toLocaleDateString()}"`,
          `"${new Date(c.expiryDate).toLocaleDateString()}"`,
          `"${info.label}"`,
        ].join(",");
      }),
    ];
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([rows.join("\n")], { type: "text/csv" }));
    a.download = `medical_certificates_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const handleReset = () => {
    setSearchTerm(""); 
    setStatusFilter("all"); 
    setRoleFilter(""); 
    setRoleSearch(""); 
    setStatusOpen(false);
    setRoleOpen(false);
    fetchCertificates();
  };

  const chartData     = getStats();
  const total         = certificates.length;
  const criticalCount = chartData[0].val + chartData[1].val;
  const idxLast       = currentPage * itemsPerPage;
  const currentItems  = filteredCertificates.slice(idxLast - itemsPerPage, idxLast);
  const totalPages    = Math.ceil(filteredCertificates.length / itemsPerPage);
  const hasFilter     = searchTerm || statusFilter !== "all" || roleFilter;
  const complianceRate = total > 0 ? ((chartData[3].val / total) * 100).toFixed(1) : 0;

  const avatarPalette = [
    "linear-gradient(135deg,#2563eb,#7c3aed)",
    "linear-gradient(135deg,#0891b2,#2563eb)",
    "linear-gradient(135deg,#059669,#0891b2)",
    "linear-gradient(135deg,#d97706,#dc2626)",
    "linear-gradient(135deg,#7c3aed,#db2777)",
  ];

  /* ── Loading ── */
  if (loading)
    return (
      <div className="admin-dash">
        <div className="admin-dash__loading">
          <div className="admin-dash__spinner" />
          <p className="admin-dash__loading-text">Loading certificates…</p>
        </div>
      </div>
    );

  return (
    <div className="admin-dash">
      <main className="">

        {/* Header */}
        <div className="admin-dash__header">
          <div>
            <h1 className="admin-dash__greeting">
              Medical <span>Certificates</span>
            </h1>
            <p className="admin-dash__subtitle">
              Compliance tracking · <strong>{total}</strong> total records · Compliance rate: <strong>{complianceRate}%</strong>
            </p>
          </div>
          <div className="admin-dash__date-pill">
            <FiCalendar />
            <span>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

        {/* KPI Stats */}
        <div className="admin-dash__stats">
          <div className="admin-dash__stat">
            <div className="admin-dash__stat-top">
              <span className="admin-dash__stat-label">Critical Risk</span>
              <div className="admin-dash__stat-icon admin-dash__stat-icon--rose">
                <FiAlertTriangle />
              </div>
            </div>
            <div className="admin-dash__stat-value"><CountUp end={criticalCount} duration={2} separator="," /></div>
            <div className="admin-dash__stat-meta">immediate action required</div>
          </div>
          <div className="admin-dash__stat">
            <div className="admin-dash__stat-top">
              <span className="admin-dash__stat-label">Due in 60 Days</span>
              <div className="admin-dash__stat-icon admin-dash__stat-icon--amber">
                <FiCalendar />
              </div>
            </div>
            <div className="admin-dash__stat-value"><CountUp end={chartData[2].val} duration={2} separator="," /></div>
            <div className="admin-dash__stat-meta">expiring soon</div>
          </div>
          <div className="admin-dash__stat">
            <div className="admin-dash__stat-top">
              <span className="admin-dash__stat-label">Active</span>
              <div className="admin-dash__stat-icon admin-dash__stat-icon--emerald">
                <FiCheckCircle />
              </div>
            </div>
            <div className="admin-dash__stat-value"><CountUp end={chartData[3].val} duration={2} separator="," /></div>
            <div className="admin-dash__stat-meta">valid certificates</div>
          </div>
          <div className="admin-dash__stat">
            <div className="admin-dash__stat-top">
              <span className="admin-dash__stat-label">Monitoring</span>
              <div className="admin-dash__stat-icon admin-dash__stat-icon--indigo">
                <FiActivity />
              </div>
            </div>
            <div className="admin-dash__stat-value"><CountUp end={total} duration={2} separator="," /></div>
            <div className="admin-dash__stat-meta">under surveillance</div>
          </div>
          <div className="admin-dash__stat">
            <div className="admin-dash__stat-top">
              <span className="admin-dash__stat-label">Total Records</span>
              <div className="admin-dash__stat-icon admin-dash__stat-icon--cyan">
                <FiList />
              </div>
            </div>
            <div className="admin-dash__stat-value"><CountUp end={total} duration={2} separator="," /></div>
            <div className="admin-dash__stat-meta">all certificates</div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="admin-dash__charts-grid">
          {/* Bar Chart */}
          <div className="admin-dash__card admin-dash__chart-wrap">
            <div className="admin-dash__card-header">
              <div>
                <h3 className="admin-dash__card-title">Compliance Health</h3>
                <p className="admin-dash__card-desc">Certificate risk distribution by status</p>
              </div>
              <FiTrendingUp className="text-xl text-gray-400" />
            </div>
            <div className="admin-dash__card-body flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 10, fontWeight: 600 }} interval={0} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#98a2b3', fontSize: 11 }} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: '#f8fafc', radius: 6 }} />
                  <Bar dataKey="val" radius={[7, 7, 0, 0]} barSize={36}>
                    {chartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Risk Breakdown */}
          <div className="admin-dash__card admin-dash__chart-wrap">
            <div className="admin-dash__card-header">
              <div>
                <h3 className="admin-dash__card-title">Risk Breakdown</h3>
                <p className="admin-dash__card-desc">Percentage analysis of compliance states</p>
              </div>
              <FiActivity className="text-xl text-rose-400" />
            </div>
            <div className="admin-dash__card-body">
              <div className="grid grid-cols-2 gap-3 mb-4">
                <QualityCard label="Critical (Expired+30d)" value={criticalCount} total={total} color="rose" />
                <QualityCard label="Due in 2 Months" value={chartData[2].val} total={total} color="amber" />
                <QualityCard label="Active" value={chartData[3].val} total={total} color="emerald" />
                <QualityCard label="Overall Monitored" value={total} total={total} color="indigo" />
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-500">Compliance Rate</span>
                  <span className="font-bold text-emerald-600">{complianceRate}%</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full" style={{ width: `${complianceRate}%` }} />
                </div>
                <div className="flex justify-between text-xs mt-2">
                  <span className="text-gray-500">Immediate Action Required</span>
                  <span className="font-bold text-rose-600">{criticalCount} records</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="emp-dash__card mb-6">
          <div className="emp-dash__card-header">
            <div>
              <h3 className="emp-dash__card-title flex items-center gap-2">
                <FiSearch className="text-blue-600" /> Filter Records
              </h3>
              <p className="emp-dash__card-desc">Search by name, ID, role, or department</p>
            </div>
          </div>
          <div className="emp-dash__card-body bg-gray-50/50">
            <div className="flex flex-wrap items-center gap-2">
              {/* Search Bar */}
              <div className="relative flex-1 min-w-[180px]">
                <FiSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  placeholder="Search name, ID..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Role Filter Button */}
              <div className="relative" ref={roleRef}>
                <button
                  onClick={() => setRoleOpen(o => !o)}
                  className={`h-8 px-3 text-xs font-medium rounded-md transition flex items-center gap-1 ${
                    roleFilter 
                      ? 'bg-blue-600 text-white hover:bg-blue-700' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  <FiBriefcase className="text-xs" /> Role {roleFilter && `: ${roleFilter}`}
                </button>
                {roleOpen && (
                  <div className="absolute z-50 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    <div className="p-2 border-b border-gray-100 bg-gray-50">
                      <div className="relative">
                        <FiSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                        <input
                          type="text"
                          className="w-full py-1 pl-7 pr-2 text-xs bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Search roles..."
                          value={roleSearch}
                          onChange={e => setRoleSearch(e.target.value)}
                          onClick={e => e.stopPropagation()}
                          autoFocus
                        />
                      </div>
                    </div>
                    <div
                      onClick={() => { setRoleFilter(""); setRoleOpen(false); setRoleSearch(""); }}
                      className={`px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer border-b border-gray-100 font-medium ${
                        !roleFilter ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      All Roles
                    </div>
                    {roles
                      .filter(r => r.name.toLowerCase().includes(roleSearch.toLowerCase()))
                      .map((role) => (
                        <div
                          key={role.id}
                          onClick={() => { setRoleFilter(role.name); setRoleOpen(false); setRoleSearch(""); }}
                          className={`px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer ${
                            roleFilter === role.name ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                          }`}
                        >
                          {role.name}
                        </div>
                      ))}
                    {roles.filter(r => r.name.toLowerCase().includes(roleSearch.toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-xs text-gray-400 text-center">No roles found</div>
                    )}
                  </div>
                )}
              </div>

              {/* Status Filter Button */}
              <div className="relative" ref={statusRef}>
                <button
                  onClick={() => setStatusOpen(o => !o)}
                  className={`h-8 px-3 text-xs font-medium rounded-md transition flex items-center gap-1 ${
                    statusFilter !== "all"
                      ? 'bg-blue-600 text-white hover:bg-blue-700' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  <FiCheckCircle className="text-xs" /> Status {statusFilter !== "all" && `: ${statusFilter}`}
                </button>
                {statusOpen && (
                  <div className="absolute z-50 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    <div
                      onClick={() => { setStatusFilter("all"); setStatusOpen(false); }}
                      className={`px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer border-b border-gray-100 font-medium ${
                        statusFilter === "all" ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      All Statuses
                    </div>
                    <div
                      onClick={() => { setStatusFilter("expired"); setStatusOpen(false); }}
                      className={`px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer ${
                        statusFilter === "expired" ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      Expired
                    </div>
                    <div
                      onClick={() => { setStatusFilter("next_month"); setStatusOpen(false); }}
                      className={`px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer ${
                        statusFilter === "next_month" ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      Expiring Soon
                    </div>
                    <div
                      onClick={() => { setStatusFilter("2_months"); setStatusOpen(false); }}
                      className={`px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer ${
                        statusFilter === "2_months" ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      Due in 2 Mo.
                    </div>
                    <div
                      onClick={() => { setStatusFilter("active"); setStatusOpen(false); }}
                      className={`px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer ${
                        statusFilter === "active" ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      Active
                    </div>
                  </div>
                )}
              </div>

              {/* Clear Filters Button */}
              {(searchTerm || statusFilter !== "all" || roleFilter) && (
                <button
                  onClick={handleReset}
                  className="h-8 px-3 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="admin-dash__card">
          <div className="admin-dash__card-header">
            <div>
              <h3 className="admin-dash__card-title">Certificate Records</h3>
              <p className="admin-dash__card-desc">
                Showing <strong>{filteredCertificates.length}</strong> of <strong>{total}</strong> records
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={downloadCSV}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 border border-emerald-200 rounded-lg text-[11px] font-bold hover:bg-emerald-600 hover:text-white transition-all"
              >
                <FaDownload size={11} /> Export
              </button>
              <span className="text-xs text-gray-500 font-medium">Rows:</span>
              <select
                value={itemsPerPage}
                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="px-2 py-1 border border-gray-200 rounded-lg text-xs text-gray-800 bg-white outline-none cursor-pointer"
              >
                {[5, 10, 20, 50].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  {["Candidate", "ID", "Reg. Date", "Exp. Date", "Status", "Actions"].map((h, i) => (
                    <th
                      key={h}
                      className={`px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider border-b border-gray-200 whitespace-nowrap ${i === 0 ? 'text-left pl-5' : 'text-center'}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((c, rowIdx) => {
                    const info = getStatusInfo(c.expiryDate);
                    const name = c.employeeName || c.candidateName || "N/A";
                    const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                    return (
                      <tr
                        key={c._id}
                        className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                      >
                        {/* Candidate */}
                        <td className="px-4 py-3 pl-5">
                          <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-md" style={{ background: avatarPalette[rowIdx % avatarPalette.length] }}>
                              {initials}
                            </div>
                            <div>
                              <div className="font-bold text-sm text-gray-800 whitespace-nowrap">{name}</div>
                              <div className="text-[10px] text-gray-500 mt-0.5">{c.jobId?.role || c.role || "—"}</div>
                            </div>
                          </div>
                        </td>

                        {/* ID */}
                        <td className="text-center px-4 py-3">
                          <span className="text-[11px] font-bold text-gray-600 bg-gray-100 px-2.5 py-1 rounded-md tracking-wide">
                            {c.employeeId || c.candidateId || "N/A"}
                          </span>
                        </td>

                        {/* Reg Date */}
                        <td className="text-center px-4 py-3 text-gray-600 text-xs font-medium">
                          {c.registrationDate ? new Date(c.registrationDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "N/A"}
                        </td>

                        {/* Exp Date */}
                        <td className="text-center px-4 py-3">
                          <span className="text-xs font-bold" style={{ color: info.text }}>
                            {c.expiryDate ? new Date(c.expiryDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "N/A"}
                          </span>
                        </td>

                        {/* Status */}
                        <td className="text-center px-4 py-3">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap" style={{ background: info.bg, color: info.text, border: `1.5px solid ${info.border}` }}>
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: info.color }} />
                            {info.label}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="text-center px-4 py-3">
                          <div className="flex items-center justify-center gap-1.5">
                            {c.documentUrl && (
                              <a
                                href={`${API_DOMAIN}${c.documentUrl}`}
                                target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-lg text-[11px] font-bold no-nowrap whitespace-nowrap hover:bg-indigo-600 hover:text-white transition-all"
                              >
                                <FiEye size={11} /> View
                              </a>
                            )}
                            {info.label !== "Active" && info.label !== "Unknown" && (
                              <button
                                onClick={() => handleSendReminder(c)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 text-rose-600 border border-rose-200 rounded-lg text-[11px] font-bold cursor-pointer whitespace-nowrap hover:bg-rose-600 hover:text-white hover:border-rose-600 transition-all"
                              >
                                <FiBell size={11} /> Remind
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center py-14 text-gray-500">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center">
                          <FiSearch size={24} className="text-gray-400" />
                        </div>
                        <div className="font-bold text-sm text-gray-600">No records found</div>
                        <div className="text-xs">Try adjusting your search or filter criteria</div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50">
              <span className="text-xs text-gray-600">
                Showing <b className="text-gray-800">{idxLast - itemsPerPage + 1}</b>–<b className="text-gray-800">{Math.min(idxLast, filteredCertificates.length)}</b> of <b className="text-gray-800">{filteredCertificates.length}</b>
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3.5 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 bg-white disabled:text-gray-400 disabled:cursor-not-allowed hover:border-gray-300 transition-colors"
                >
                  ← Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .reduce((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i - 1] > 1) acc.push("…");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) => (
                    <button
                      key={i}
                      onClick={() => typeof p === "number" && setCurrentPage(p)}
                      disabled={p === "…"}
                      className={`px-3 py-1.5 border rounded-lg text-xs font-bold ${p === currentPage ? 'border-indigo-500 bg-indigo-500 text-white shadow-md' : 'border-gray-200 text-gray-800 bg-white hover:border-gray-300'} disabled:cursor-default transition-colors`}
                    >
                      {p}
                    </button>
                  ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3.5 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 bg-white disabled:text-gray-400 disabled:cursor-not-allowed hover:border-gray-300 transition-colors"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>

      </main>
    </div>
  );
};

export default AllMedicalCertificate;